import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Config from "../../../Config";
import { useWindowDimensions } from "../../../utils";
import { GAMES_CATALOG } from "../Games/gamesCatalog";
import { getGamePreviewUrl } from "../Games/gamesUtils";
import {
  OnestBoldDefault,
  OnestNormalDefault,
  OnestNormalMed,
} from "../../styled/TextComponents";
import {
  color_grey_light,
  color_grey_ultra_light,
  color_red_active,
  color_red_default,
  color_red_ultra_light,
  color_white,
} from "../../../constants/colors";
import { getUpdateIconComponent } from "./updatesConstants";
import {
  resolveUpdateDisplay,
  resolveUpdateIconKey,
  getUpdateResourceId,
} from "./updateContent";
import { getLocalizedLibraryPath } from "../../../i18n/localizedContent";
import { useI18n } from "../../../i18n/I18nProvider";

const updatesTextPrimary = "#171717";
const updatesTextSecondary = "#424242";
const updatesTextMeta = "#737373";
const updatesIconDefault = "#525252";

const DateBadge = ({ date, compact, dense }) => {
  const { locale } = useI18n();
  const format = compact || dense ? "D MMMM YYYY" : "LL";
  return (
    <OnestNormalMed
      style={{
        fontSize: compact || dense ? 12 : 14,
        lineHeight: 1.3,
        color: updatesTextMeta,
        fontWeight: 450,
        letterSpacing: "-0.1px",
      }}
    >
      {moment(date).locale(locale === "en" ? "en-gb" : "ru").format(format)}
    </OnestNormalMed>
  );
};

const UpdateCardView = ({
  change,
  featured = false,
  showTimeline = false,
  previewLocale,
}) => {
  const { locale, t } = useI18n();
  const displayLocale = previewLocale || locale;
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const libraryFiles = useSelector((state) => state.libraryFiles);
  const bodyRef = React.useRef(null);
  const [expanded, setExpanded] = React.useState(false);
  const [bodyIsClipped, setBodyIsClipped] = React.useState(false);
  const isMobile =
    screenWidth <= 650 || (screenHeight <= 500 && screenWidth <= 950);
  const denseDesktop = !isMobile && screenHeight <= 800;
  const resourceId = getUpdateResourceId(change);
  const libraryFile =
    change?.type === "library"
      ? libraryFiles.find((file) => +file.id === +resourceId)
      : null;
  const selectedGame =
    change?.type === "game"
      ? GAMES_CATALOG.find((item) => +item.id === +resourceId)
      : null;
  const legacyGame =
    !selectedGame && change?.type === "custom"
      ? GAMES_CATALOG.find((item) =>
          String(change?.text || "")
            .toLocaleLowerCase()
            .includes(
              item.name.toLocaleLowerCase().replace(/[?!.,]+$/g, ""),
            ),
        )
      : null;
  const game = selectedGame || legacyGame;
  const showThumbnail = !isMobile && Boolean(game);
  const shouldRender =
    change?.type === "library"
      ? Boolean(libraryFile || change.text?.trim() || change.text_en?.trim())
      : Boolean(change?.text?.trim() || change?.text_en?.trim());

  const { title, body } = resolveUpdateDisplay(
    change,
    libraryFile,
    t,
    displayLocale,
  );
  const iconKey = game ? "gamepad" : resolveUpdateIconKey(change);
  const Icon = getUpdateIconComponent(iconKey);
  const localizedLibraryPath = getLocalizedLibraryPath(libraryFile, displayLocale);

  const materialHref =
    localizedLibraryPath && `${Config.CLOUD_ADDR}/${localizedLibraryPath}`;
  const gameHref = game?.link;
  const actionHref = materialHref || gameHref;
  const actionLabel = game ? t("Открыть игру →") : t("Открыть материал →");
  const thumbnailSrc = libraryFile?.image_path
    ? `${Config.CLOUD_ADDR}/${libraryFile.image_path}`
    : game
      ? getGamePreviewUrl(game.tile_image_path ?? game.image_path)
      : null;

  React.useEffect(() => {
    setExpanded(false);
  }, [change?.id]);

  React.useEffect(() => {
    const bodyElement = bodyRef.current;
    if (!bodyElement || expanded) return;

    const checkOverflow = () => {
      setBodyIsClipped(
        bodyElement.scrollHeight > bodyElement.clientHeight + 1,
      );
    };

    checkOverflow();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(bodyElement);
    return () => observer.disconnect();
  }, [body, denseDesktop, expanded, isMobile, screenWidth]);

  if (!shouldRender) {
    return null;
  }

  const cardStyle = {
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "stretch" : "center",
    gap: isMobile ? 12 : denseDesktop ? 14 : 20,
    padding: featured
      ? isMobile
        ? 16
        : denseDesktop
          ? "14px 18px"
          : 20
      : isMobile
        ? 16
        : denseDesktop
          ? "12px 16px"
          : 18,
    borderRadius: featured ? 20 : 16,
    backgroundColor: featured ? "#fff5f5" : color_white,
    border: `1px solid ${
      featured ? color_red_ultra_light : color_grey_light
    }`,
  };

  const timelineDot =
    showTimeline && !isMobile ? (
      <div
        style={{
          position: "absolute",
          // Feed line is at x=11px, card starts after 28px padding:
          // -22 + half of the 10px dot = -17, exactly back to x=11.
          left: -22,
          top: "50%",
          transform: "translateY(-50%)",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: featured ? color_red_default : color_white,
          border: `2px solid ${color_grey_light}`,
          boxSizing: "border-box",
        }}
      />
    ) : null;

  return (
    <article style={cardStyle}>
      {timelineDot}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          alignItems: isMobile ? "center" : "flex-start",
          gap: isMobile ? 12 : 0,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: isMobile ? 36 : denseDesktop ? 38 : featured ? 48 : 40,
            height: isMobile ? 36 : denseDesktop ? 38 : featured ? 48 : 40,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: featured ? color_white : color_grey_ultra_light,
            color: featured ? color_red_active : updatesIconDefault,
            fontSize: isMobile ? 18 : denseDesktop ? 20 : 22,
          }}
        >
          <Icon />
        </div>
        {isMobile && <DateBadge date={change.time_create} compact />}
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: denseDesktop ? 3 : 8,
        }}
      >
        {!isMobile && (
          <DateBadge date={change.time_create} dense={denseDesktop} />
        )}
        <OnestBoldDefault
          style={{
            fontSize: isMobile ? 17 : denseDesktop ? 16 : 18,
            lineHeight: 1.3,
            fontWeight: 650,
            letterSpacing: "-0.15px",
            color: updatesTextPrimary,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </OnestBoldDefault>
        {body ? (
          <OnestNormalDefault
            ref={bodyRef}
            style={{
              fontSize: isMobile ? 15 : denseDesktop ? 13 : 16,
              lineHeight: denseDesktop ? 1.35 : 1.5,
              color: updatesTextSecondary,
              fontWeight: 400,
              letterSpacing: "-0.05px",
              overflowWrap: "anywhere",
              ...(expanded
                ? {}
                : {
                    display: "-webkit-box",
                    WebkitLineClamp: isMobile ? 3 : denseDesktop ? 2 : 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }),
            }}
          >
            {body.split("\n").map((line, idx, arr) => (
              <React.Fragment key={idx}>
                {line}
                {idx < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </OnestNormalDefault>
        ) : null}
        {(bodyIsClipped || expanded) && (
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            style={{
              alignSelf: "flex-start",
              padding: 0,
              border: "none",
              background: "transparent",
              color: color_red_active,
              fontFamily: "Onest, sans-serif",
              fontSize: denseDesktop ? 12 : 14,
              fontWeight: 600,
              lineHeight: 1.4,
              cursor: "pointer",
            }}
          >
            {expanded ? t("Свернуть") : t("Читать полностью")}
          </button>
        )}
        {actionHref && (
          <a
            href={actionHref}
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: "Onest, sans-serif",
              fontSize: denseDesktop ? 12 : 14,
              fontWeight: 600,
              color: color_red_active,
              textDecoration: "none",
            }}
          >
            {actionLabel}
          </a>
        )}
      </div>

      {showThumbnail && thumbnailSrc && (
        <a
          href={actionHref}
          target="_blank"
          rel="noreferrer"
          style={{ flexShrink: 0 }}
        >
          <img
            src={thumbnailSrc}
            alt=""
            style={{
              width: denseDesktop ? 96 : 110,
              height: denseDesktop ? 56 : 72,
              borderRadius: denseDesktop ? 10 : 12,
              objectFit: "cover",
              border: `1px solid ${color_grey_light}`,
            }}
          />
        </a>
      )}
    </article>
  );
};

export default UpdateCardView;
